import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../../uiComponents/MuiTable/StyledTable.js';
import MuiTableHeader from '../../../../uiComponents/MuiTable/MuiTableHeader.js';
import { stableSort, getComparator, handleRequestSort } from '../../../../uiComponents/MuiTable/MuiHelper';
import { TableBody, TableContainer, Button } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import ClirRiskResultsModal from '../ClirRiskResultsModal/index.js';

const PatientDetailsClirRiskTable = ({ data, flaggedSort }) => {
  if (!data || data.length === 0) return null;

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('conditionName');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  const isFlaggedSort = flaggedSort;

  const handleOpenModal = (row) => {
    setModalRow(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalRow(null);
  };

  const headCells = [
    { label: 'Condition', id: 'conditionName', numeric: false, disablePadding: false },
    { label: 'Risk Score', id: 'score', numeric: true, disablePadding: false, align: 'left' },
    { label: 'Percentile', id: 'percentile', numeric: true, disablePadding: false, align: 'left' },
    { label: 'Guideline', id: 'guideLine', numeric: false, disablePadding: false },
    { label: 'Flagged', id: 'isInformative', numeric: false, disablePadding: false },
    { label: 'Results', id: 'results', numeric: false, disablePadding: false, type: 'noSort' },
  ];

  return (
    <>
      <TableContainer component={StyledPaper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
        <StyledTable
          size='small'
          aria-label="CLIR risk scores table"
          key={JSON.stringify(data)}
        >
          <MuiTableHeader
            stickyHeader={true}
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={(event, property) => handleRequestSort(event, property, order, orderBy, setOrder, setOrderBy)}
            hideSortArrows={isFlaggedSort}
          />
          <TableBody>
            {(isFlaggedSort ? data : stableSort(data, getComparator(order, orderBy))).map((row, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell>{row.conditionName || '-'}</StyledTableCell>
                <StyledTableCell>
                  {row.score !== null && row.score !== undefined ? Number(row.score).toFixed(2) : ''}
                </StyledTableCell>
                <StyledTableCell>
                  {row.percentile !== null && row.percentile !== undefined ? (row.percentile * 100).toFixed(1) + '%' : ''}
                </StyledTableCell>
                <StyledTableCell>{row.guideLine || ''}</StyledTableCell>
                <StyledTableCell>
                  {row.isInformative ? (
                    <span
                      role="img"
                      aria-label="Flagged informative condition"
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <FlagIcon sx={{ color: 'var(--primary-color)' }} fontSize="medium" aria-hidden="true" />
                      <span style={{ position: 'absolute', left: -9999 }}>Flagged</span>
                    </span>
                  ) : null}
                </StyledTableCell>
                <StyledTableCell style={{width: '25%'}}>
                  <Button
                    variant="outlined"
                    aria-label="view results"
                    onClick={() => handleOpenModal(row)}
                    startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                  >
                    View Full Results
                  </Button>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <ClirRiskResultsModal open={modalOpen} onClose={handleCloseModal} row={modalRow} />
    </>
  );
};

PatientDetailsClirRiskTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      conditionName: PropTypes.string,
      score: PropTypes.number,
      percentile: PropTypes.number,
      guideLine: PropTypes.string,
      toolName: PropTypes.string,
      caseCount: PropTypes.number,
      isInformative: PropTypes.bool,
      toolExceptions: PropTypes.array,
      calculationErrors: PropTypes.array,
    })
  ).isRequired,
};

export default PatientDetailsClirRiskTable;